import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Palette, Shield, TrendingUp, Truck, CheckCircle, Scissors, Chrome, Layers } from "lucide-react";
import heroVinylWraps from "@/assets/hero-vinyl-wraps.webp";

const VinylWraps = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Vinyl Car Wraps Las Vegas | Color Change & Custom Graphics</title>
        <meta name="description" content="Transform your vehicle with premium vinyl wraps in Las Vegas. Full color changes, commercial fleet wraps & custom graphics. Matte, gloss, satin finishes. Free quote." />
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={heroVinylWraps} 
            alt="Matte Black Vinyl Wrap Luxury Vehicle Las Vegas" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-darker-bg/90 to-background/95" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl animate-fade-in">
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6 text-foreground">
              Vinyl Wraps & Graphics
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Transform your vehicle with premium vinyl wraps and professional graphics for personal style or commercial branding
            </p>
            <Button asChild size="lg" variant="premium">
              <Link to="/contact">Request Free Quote</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
              Why Choose Vinyl Wraps?
            </h2>
            <p className="text-xl text-muted-foreground">
              Transform your vehicle's appearance with premium vinyl wraps. Choose from thousands of colors, finishes, and textures including matte, satin, gloss, chrome, and color-shift options.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <Shield className="w-12 h-12 text-accent mb-4" />
                <h3 className="text-xl font-heading font-bold mb-3">Paint Protection</h3>
                <p className="text-muted-foreground">
                  Protects your original paint from chips, scratches, and environmental damage while adding style
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <Palette className="w-12 h-12 text-accent mb-4" />
                <h3 className="text-xl font-heading font-bold mb-3">Endless Customization</h3>
                <p className="text-muted-foreground">
                  Thousands of color and finish options to create your perfect look - matte, gloss, satin, chrome, and more
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <TrendingUp className="w-12 h-12 text-accent mb-4" />
                <h3 className="text-xl font-heading font-bold mb-3">Reversible</h3>
                <p className="text-muted-foreground">
                  Completely removable without damage to original paint - perfect for leases or style changes
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Customization Options Section */}
      <section className="py-20 bg-gradient-to-b from-background to-darker-bg">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                Customization Options
              </h2>
              <p className="text-xl text-muted-foreground">
                From complete color changes to commercial branding - we bring your vision to life
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="bg-[#2A2A2A] border-silver transition-all duration-300 hover:shadow-[var(--shadow-gold-glow)]">
                <CardContent className="p-6">
                  <Palette className="w-12 h-12 text-accent mb-4" />
                  <h3 className="text-xl font-heading font-bold mb-3">Full Color Change</h3>
                  <p className="text-muted-foreground">
                    Completely transform the look of your vehicle with thousands of colors and finishes (Matte, Satin, Gloss)
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-[#2A2A2A] border-silver transition-all duration-300 hover:shadow-[var(--shadow-gold-glow)]">
                <CardContent className="p-6">
                  <Scissors className="w-12 h-12 text-accent mb-4" />
                  <h3 className="text-xl font-heading font-bold mb-3">Chrome Deletes</h3>
                  <p className="text-muted-foreground">
                    Eliminate chrome trim for a sleek, modern, blackout aesthetic
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-[#2A2A2A] border-silver transition-all duration-300 hover:shadow-[var(--shadow-gold-glow)]">
                <CardContent className="p-6">
                  <Layers className="w-12 h-12 text-accent mb-4" />
                  <h3 className="text-xl font-heading font-bold mb-3">Accents & Restyling</h3>
                  <p className="text-muted-foreground">
                    Custom roof wraps, hood accents, and mirror caps to make your vehicle unique
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-[#2A2A2A] border-silver transition-all duration-300 hover:shadow-[var(--shadow-gold-glow)]">
                <CardContent className="p-6">
                  <Truck className="w-12 h-12 text-accent mb-4" />
                  <h3 className="text-xl font-heading font-bold mb-3">Commercial Fleets</h3>
                  <p className="text-muted-foreground">
                    Branding and protection for business vehicles and fleets
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Full Color Change Wraps Section */}
      <section className="py-20 bg-gradient-to-b from-background to-darker-bg">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                Full Color Change Wraps
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Give your vehicle a complete transformation with a full color change wrap. From subtle elegance to bold statements, we bring your vision to life.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 mb-12">
              <div>
                <h3 className="text-2xl font-heading font-bold mb-6">Wrap Features</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Thousands of color options - any shade imaginable</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Multiple finishes: matte, satin, gloss, chrome, metallic</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Textured options including carbon fiber and brushed metal</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Color-shift and chameleon finishes for unique looks</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Full vehicle transformations or accent pieces</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Roof, hood, mirror, and trim accent wraps</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-heading font-bold mb-6">Benefits</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Protects original factory paint underneath</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Completely customizable to your preferences</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Removable without damage - perfect for leases</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Cost-effective alternative to custom paint</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Faster installation than traditional paint jobs</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Easy to change your look in the future</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commercial & Branding Graphics Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <Truck className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                Commercial & Branding Graphics
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Transform your company vehicle into a mobile billboard. We design and install everything from full fleet wraps to custom business logos and decals, turning drive time into advertising time.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 mb-12">
              <div>
                <h3 className="text-2xl font-heading font-bold mb-6">Commercial Services</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Full fleet wraps with consistent branding</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Partial vehicle graphics and lettering</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Custom logo design and implementation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Professional vehicle lettering</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Magnetic signs for temporary branding</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Window perforated graphics for visibility</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-heading font-bold mb-6">Business Benefits</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">24/7 mobile advertising wherever you drive</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Professional brand presence and credibility</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Consistent fleet branding across all vehicles</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">High-impact marketing at low cost per impression</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Durable and weather-resistant materials</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Increased brand awareness and recognition</span>
                  </li>
                </ul>
              </div>
            </div>

            <Card className="bg-card border-accent/50">
              <CardContent className="p-8">
                <h3 className="text-2xl font-heading font-bold mb-4">Turn Your Fleet Into Moving Billboards</h3>
                <p className="text-muted-foreground mb-6">
                  Vehicle wraps are one of the most cost-effective forms of advertising, generating thousands of impressions daily. Whether you have one vehicle or an entire fleet, we'll help you maximize your mobile marketing potential with professional design and flawless installation.
                </p>
                <Button asChild variant="premium">
                  <Link to="/contact">Request Commercial Quote</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Wrap Finishes Section */}
      <section className="py-20 bg-gradient-to-b from-darker-bg to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                Available Finishes
              </h2>
              <p className="text-xl text-muted-foreground">
                Choose from a wide variety of finishes to achieve your perfect look
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-3">Gloss Finish</h3>
                  <p className="text-muted-foreground">
                    Classic high-shine finish that enhances color depth and creates a wet look similar to factory paint
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-3">Matte Finish</h3>
                  <p className="text-muted-foreground">
                    Modern, sophisticated look with no shine. Popular for luxury and exotic vehicles
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-3">Satin Finish</h3>
                  <p className="text-muted-foreground">
                    Perfect middle ground between gloss and matte with subtle shine and elegant appearance
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-3">Chrome/Metallic</h3>
                  <p className="text-muted-foreground">
                    Ultra-reflective mirror-like finish for maximum attention and show-stopping looks
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-3">Color-Shift</h3>
                  <p className="text-muted-foreground">
                    Changes color based on viewing angle - creates stunning, dynamic appearance
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-3">Textured</h3>
                  <p className="text-muted-foreground">
                    Carbon fiber, brushed metal, and other textured patterns for unique styling
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent/20 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            Ready to Transform Your Vehicle?
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Get a custom quote for vinyl wraps or commercial graphics
          </p>
          <Button asChild variant="hero" size="lg">
            <Link to="/contact">Request Free Quote</Link>
          </Button>
        </div>
      </section>

      {/* Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Vehicle Vinyl Wraps and Graphics",
          "provider": {
            "@type": "AutomotiveBusiness",
            "name": "Battle Born Protection Films",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "6960 W Russell Rd B",
              "addressLocality": "Las Vegas",
              "addressRegion": "NV",
              "postalCode": "89113"
            }
          },
          "areaServed": {
            "@type": "City",
            "name": "Las Vegas"
          },
          "description": "Full color change vinyl wraps and custom commercial graphics for vehicles. Transform your vehicle's appearance or turn your fleet into mobile billboards."
        })}
      </script>
    </div>
  );
};

export default VinylWraps;
